/**
 * Document Service
 * Gestisce referti, download PDF, immagini DICOM
 */

import { BaseService, ApiError } from './BaseService';
import { Report, DicomStudy, DicomJobProgress } from '../../types';
import AuthService from './AuthService';
import logger from '../Logger';
import auditLogger from '../AuditLogger';
import sanitizer from '../Sanitizer';

interface DocumentListResponse {
  documents: Report[];
  totalCount?: number;
}

interface DicomCheckResponse {
  available: boolean;
  studyUID?: string;
  studyUIDs?: string[];  // Array di study UIDs
  instanceUIDs?: string[];  // Array di instance UIDs (serie DICOM)
  patientID?: string;
  message?: string;
}

interface DicomJobResponse {
  jobId: string;
  status: string;
  downloadUrl?: string;
}

class DocumentService extends BaseService {
  private static instance: DocumentService;
  private pdfCache: Map<string, string> = new Map();
  private cacheTimeout: number = 5 * 60 * 1000; // 5 minuti

  private constructor() {
    super();
  }

  static getInstance(): DocumentService {
    if (!DocumentService.instance) {
      DocumentService.instance = new DocumentService();
    }
    return DocumentService.instance;
  }

  /**
   * Ottiene la lista dei referti
   */
  async getReports(username: string): Promise<Report[]> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    const sanitizedUsername = sanitizer.sanitizeText(username);

    try {
      // Usa un timeout esteso per questa chiamata che può essere lenta
      const response = await this.request<Report[]>(
        `api/Document/list?username=${encodeURIComponent(sanitizedUsername)}`,
        {
          method: 'GET',
          headers: this.withAuth(token),
          timeout: 120000 // 120 secondi per questa chiamata specifica
        }
      );

      // Ordina per data più recente
      const sortedReports = this.sortReportsByDate(response);

      logger.info(`Fetched ${sortedReports.length} reports for user`);

      return sortedReports;
    } catch (error: any) {
      if (error.statusCode === 404) {
        return []; // Nessun referto trovato
      }
      throw error;
    }
  }

  /**
   * Scarica un referto PDF
   */
  async downloadReport(episodeId: string, username: string, documentId?: string, useCache: boolean = true): Promise<string> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    const sanitizedEpisodeId = sanitizer.sanitizeText(episodeId);
    const cacheKey = documentId ? `${username}_${sanitizedEpisodeId}_${documentId}` : `${username}_${sanitizedEpisodeId}`;

    // Check cache
    if (useCache && this.pdfCache.has(cacheKey)) {
      const cached = this.pdfCache.get(cacheKey);
      if (cached) {
        logger.debug('Returning cached PDF for:', episodeId, documentId);
        return cached;
      }
    }

    try {
      let url = `api/Document/download?episodeId=${encodeURIComponent(sanitizedEpisodeId)}&username=${encodeURIComponent(username)}`;
      if (documentId) {
        url += `&documentId=${documentId}`;
      }

      const response = await this.request<{ content: string }>(
        url,
        {
          method: 'GET',
          headers: this.withAuth(token),
          timeout: 60000 // 60 secondi per il download PDF
        }
      );

      if (!response.content) {
        throw this.createError('PDF content not found', 404);
      }

      // Cache the PDF
      this.pdfCache.set(cacheKey, response.content);
      
      // Auto-clear cache after timeout
      setTimeout(() => {
        this.pdfCache.delete(cacheKey);
      }, this.cacheTimeout);

      logger.info('PDF downloaded successfully:', episodeId, documentId);
      
      // Audit log for report download
      auditLogger.logReportDownload(episodeId, username);
      
      return response.content;
    } catch (error) {
      logger.error('Failed to download PDF:', error);
      throw error;
    }
  }

  /**
   * Scarica PDF come Blob
   */
  async downloadReportAsBlob(episodeId: string, username: string, documentId?: string): Promise<Blob> {
    const base64Content = await this.downloadReport(episodeId, username, documentId);
    
    // Converti base64 in Blob
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: 'application/pdf' });
  }

  /**
   * Verifica disponibilità immagini DICOM
   */
  async checkDicomAvailability(accessionNumber: string): Promise<DicomCheckResponse> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      // IMPORTANTE: L'API richiede il parametro come query string, non nel body
      const apiResponse = await this.post<any>(
        `api/DICOM/CheckDICOMAvailabilityByAccNum?accNum=${encodeURIComponent(accessionNumber)}`,
        {} // Body vuoto, il parametro è nel query string
      );

      // Mappa la risposta API al formato atteso dal frontend
      const response: DicomCheckResponse = {
        available: apiResponse.hasDICOMImages === true,
        studyUIDs: apiResponse.sStudyUIDs || [],
        instanceUIDs: apiResponse.sInstanceUIDs || [],  // Aggiungi instanceUIDs
        patientID: apiResponse.patientId || apiResponse.patientID || '',
        message: apiResponse.message
      };

      logger.info('DICOM availability check:', response.available ? 'Available' : 'Not available');
      
      return response;
    } catch (error) {
      logger.error('Failed to check DICOM availability:', error);
      throw error;
    }
  }

  /**
   * Avvia job di export DICOM
   */
  async startDicomExport(studyUID: string, patientID: string): Promise<string> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      // IMPORTANTE: Il backend richiede FormData, non JSON
      const formData = new FormData();
      formData.append('studyUID', sanitizer.sanitizeText(studyUID));
      formData.append('patientID', sanitizer.sanitizeText(patientID));

      // Usa postFormData invece di post per inviare FormData
      const response = await this.postFormData<DicomJobResponse>(
        'api/DICOM/PublishAndDownloadJob',
        formData
      );

      if (!response.jobId) {
        throw this.createError('Job ID not received from server', 500);
      }

      logger.info('DICOM export job started:', response.jobId);
      
      return response.jobId;
    } catch (error) {
      logger.error('Failed to start DICOM export:', error);
      throw error;
    }
  }

  /**
   * Controlla lo stato di un job DICOM
   */
  async checkDicomJobStatus(jobId: string): Promise<DicomJobProgress> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      const response = await this.post<DicomJobProgress>(
        `api/DICOM/GetJobStatus?jobId=${encodeURIComponent(jobId)}`,
        {} // Body vuoto, il parametro è nel query string
      );

      return response;
    } catch (error) {
      logger.error('Failed to check job status:', error);
      throw error;
    }
  }

  /**
   * Scarica ZIP con immagini DICOM
   */
  async downloadDicomZip(jobId: string): Promise<Blob> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      const response = await fetch(
        `${this.baseURL}api/DICOM/DownloadZip?jobId=${encodeURIComponent(jobId)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw this.createError(`Failed to download DICOM ZIP: ${response.statusText}`, response.status);
      }

      const blob = await response.blob();
      
      logger.info('DICOM ZIP downloaded successfully');
      
      // Audit log for DICOM download
      const patientId = localStorage.getItem('codiceFiscale') || 'unknown';
      auditLogger.logDicomDownload('unknown_study', patientId, jobId);
      
      return blob;
    } catch (error) {
      logger.error('Failed to download DICOM ZIP:', error);
      throw error;
    }
  }

  /**
   * Ripubblica un referto (legacy - ripubblicazione diretta)
   */
  async republishReport(episodeId: string): Promise<void> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      await this.post('api/Document/Republish', {
        episodeId: sanitizer.sanitizeText(episodeId)
      });

      logger.info('Report republished:', episodeId);
    } catch (error) {
      logger.error('Failed to republish report:', error);
      throw error;
    }
  }

  /**
   * Richiede la ripubblicazione di un referto con conferma via email.
   * Invia un'email al paziente con un link di conferma.
   */
  async requestRepublishReport(episodeId: string): Promise<{ message: string; emailSent: boolean }> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      const response = await this.post('api/Document/republish-request', {
        examinationMnemonicCodeFull: sanitizer.sanitizeText(episodeId)
      });

      logger.info('Republish request sent for:', episodeId);
      return response as { message: string; emailSent: boolean };
    } catch (error) {
      logger.error('Failed to request republish:', error);
      throw error;
    }
  }

  /**
   * Forza notifica di un referto (solo admin)
   */
  async forceNotifyReport(episodeId: string, confirmed: boolean = false): Promise<any> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      const response = await this.post<any>('api/Document/force-notify', {
        examinationMnemonicCodeFull: sanitizer.sanitizeText(episodeId),
        confirmed: confirmed
      });

      logger.info('Force notify report:', episodeId, confirmed);
      return response;
    } catch (error) {
      logger.error('Failed to force notify report:', error);
      throw error;
    }
  }

  /**
   * Ottiene referti online
   */
  async getOnlineReports(username: string): Promise<Report[]> {
    const token = AuthService.getToken();
    if (!token) {
      throw this.createError('Non autenticato', 401);
    }

    try {
      const response = await this.get<Report[]>(
        `api/Document/online?username=${encodeURIComponent(username)}`
      );

      return this.sortReportsByDate(response);
    } catch (error: any) {
      if (error.statusCode === 404) {
        return [];
      }
      throw error;
    }
  }

  /**
   * Pulisce la cache
   */
  clearCache(): void {
    this.pdfCache.clear();
    logger.debug('Document cache cleared');
  }

  /**
   * Ottiene dimensione cache
   */
  getCacheSize(): number {
    return this.pdfCache.size;
  }

  /**
   * Helper: ordina referti per data
   */
  private sortReportsByDate(reports: Report[]): Report[] {
    return [...reports].sort((a, b) => {
      const dateA = this.parseDate(a.registrationDate);
      const dateB = this.parseDate(b.registrationDate);
      return dateB.getTime() - dateA.getTime(); // Più recenti prima
    });
  }

  /**
   * Helper: parse date in formato dd/MM/yyyy o ISO
   */
  private parseDate(dateStr: string | Date): Date {
    if (!dateStr) return new Date(0);
    
    // Se già Date, ritorna direttamente
    if (dateStr instanceof Date) return dateStr;
    
    // Check formato dd/MM/yyyy
    const parts = dateStr.split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      return new Date(+parts[2], +parts[1] - 1, +parts[0]);
    }
    
    // Altrimenti assume ISO
    return new Date(dateStr);
  }

  /**
   * Helper per creare errori
   */
  protected createError(message: string, statusCode?: number, details?: any): ApiError {
    return super.createError(message, statusCode, details);
  }
}

export default DocumentService.getInstance();